import downloadjs from "downloadjs";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { downloadMimeType } from "ui/Scripts/source/models/consts";
import { ReportType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "source/framework/appUtils";
import { IReportGenerated2, ReportGenerated2 } from "source/models/report/reportGenerated2";
import { IReportItem } from "source/models/report/reportItem";
import { IReportParameterValue } from "source/models/report/reportParameterValue";
import * as recordService from "source/models/services/recordService";
import { PageReport } from "source/components/framework/Report/PageReport";
import { RecordPageListReportHtml } from "./RecordPageListReportHtml";

export interface IRecordPageListReportProp {
    companyId: string;
    reports: IReportItem[];
    reportId: string;
    selectedEmployeeTypeIds: string[];
    selectedJobTitleIds: string[];
    selectedUnitIds: string[];
    selectedActivityFilterId: string;
    dataId?: string;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

interface IRecordPageListReportState {
    isLoading: boolean;
    report: IReportItem;
    reportGenerated: IReportGenerated2;
    reportDataUri: string;
    employeeTypes: number[];
    jobTitles: string[];
    unitIds: string[];
    activityState: number;
    selectedId: string;
    checkedIds: string[];
}

export class RecordPageListReport extends React.Component<IRecordPageListReportProp, IRecordPageListReportState> {
    parameterValues: IReportParameterValue[] = [];

    constructor(props: IRecordPageListReportProp) {
        super(props);
        const report = props.reportId
            ? props.reports.find(i => i.id === props.reportId)
            : null;
        this.parameterValues = report
            ? report.getReportParameterValues()
            : [];
        this.state = {
            isLoading: false,
            reportGenerated: null,
            reportDataUri: "",
            report,
            employeeTypes: props.selectedEmployeeTypeIds
                ? props.selectedEmployeeTypeIds.filter(i => !Base.isNullOrEmpty(i)).map(i => i.toInteger())
                : [],
            jobTitles: props.selectedJobTitleIds
                ? props.selectedJobTitleIds.filter(i => !!i)
                : [],
            unitIds: props.selectedUnitIds
                ? props.selectedUnitIds.filter(i => i !== Base.emptyGuid)
                : [],
            activityState: props.selectedActivityFilterId
                ? parseInt(props.selectedActivityFilterId, 10)
                : 0,
            selectedId: "",
            checkedIds: []
        };
    }

    createReport = async (report: IReportItem, employeeTypes: number[], jobTitles: string[], unitIds: string[], activityState: number) => {
        if (!report) return;
        const props = this.props;
        let reportGenerated: ReportGenerated2 = null;
        if (report.type !== ReportType.Html) {
            this.setState({ isLoading: true });
            reportGenerated = await AppUtils.callService(() => recordService.getReportGenerated(props.companyId, report.reportId, report.title, employeeTypes, jobTitles, unitIds, activityState, this.parameterValues),
                false, false, () => { this.setState({ isLoading: false }); });
            if (!reportGenerated) return;
        }
        const reportBlob = reportGenerated && reportGenerated.content ? Base.base64ToBlob(reportGenerated.content, reportGenerated.mediaType) : null;
        const reportDataUri = reportBlob ? await Base.blobToBase64(reportBlob) : "";
        this.setState({
            report,
            reportGenerated,
            reportDataUri,
            employeeTypes,
            jobTitles,
            unitIds,
            activityState
        });
    };

    componentDidMount(): void {
        const state = this.state;
        this.createReport(state.report, state.employeeTypes, state.jobTitles, state.unitIds, state.activityState);
    }

    componentDidUpdate(prevProps: IRecordPageListReportProp, prevState: IRecordPageListReportState) {
        const props = this.props;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevReportId = prevProps.reportId ? prevProps.reportId : "";
        const reportId = props.reportId ? props.reportId : "";
        const prevReportIds = prevProps.reports ? prevProps.reports.map(i => i.id) : [];
        const reportIds = props.reports ? props.reports.map(i => i.id) : [];
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevEmployeeTypes = prevProps.selectedEmployeeTypeIds ? prevProps.selectedEmployeeTypeIds.filter(i => !Base.isNullOrEmpty(i)).map(i => i.toInteger()) : [];
        const employeeTypes = props.selectedEmployeeTypeIds ? props.selectedEmployeeTypeIds.filter(i => !Base.isNullOrEmpty(i)).map(i => i.toInteger()) : [];
        const prevJobTitles = prevProps.selectedJobTitleIds ? prevProps.selectedJobTitleIds.filter(i => !!i) : [];
        const jobTitles = props.selectedJobTitleIds ? props.selectedJobTitleIds.filter(i => !!i) : [];
        const prevUnitIds = prevProps.selectedUnitIds ? prevProps.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [];
        const unitIds = props.selectedUnitIds ? props.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [];
        const prevActivityState = prevProps.selectedActivityFilterId ? parseInt(prevProps.selectedActivityFilterId, 10) : 0;
        const activityState = props.selectedActivityFilterId ? parseInt(props.selectedActivityFilterId, 10) : 0;
        const report = reportId ? props.reports.find(i => i.id === reportId) : null;
        const parameterValues = report ? report.getReportParameterValues() : [];
        if (dataId === prevDataId && reportId === prevReportId && companyId === prevCompanyId &&
            Base.equalArrays(prevEmployeeTypes, employeeTypes) &&
            Base.equalStrArrays(prevJobTitles, jobTitles) &&
            Base.equalStrArrays(prevUnitIds, unitIds) &&
            Base.equalStrArrays(prevReportIds, reportIds) &&
            JSON.stringify(this.parameterValues) === JSON.stringify(parameterValues) && prevActivityState === activityState) return;
        this.parameterValues = parameterValues;
        this.setState({
            selectedId: "",
            checkedIds: []
        }, () => this.createReport(reportId ? props.reports.find(i => i.id === reportId) : null, employeeTypes, jobTitles, unitIds, activityState));
    }

    handleDownload = async () => {
        const props = this.props;
        const state = this.state;
        const report = state.report;
        let reportGenerated = state.reportGenerated;
        if (!report || !reportGenerated || !reportGenerated.content) return;
        const excelTable = reportGenerated.tables.length > 0 ? reportGenerated.tables[0] : null;
        if (!excelTable) return;
        if (report.type === ReportType.Excel && excelTable.taskType.lineMultiselect && state.checkedIds.length !== 0 && state.checkedIds.length !== excelTable.dataRows.length) {
            this.setState({ isLoading: true });
            const printRows = state.checkedIds.map(i => excelTable.dataRows.findIndex(j => j.id === i)).filter(i => i > -1);
            reportGenerated = await AppUtils.callService(() => recordService.getReportGenerated(props.companyId, report.reportId, report.title, state.employeeTypes, state.jobTitles, state.unitIds, state.activityState, this.parameterValues, printRows),
                false, false, () => { this.setState({ isLoading: false }); });
            if (!reportGenerated) return;
        }
        const blob = Base.base64ToBlob(reportGenerated.content, downloadMimeType);
        if (!blob) return;
        downloadjs(blob, reportGenerated.fileName);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <PageReport
                isLoading={state.isLoading}
                report={state.report}
                reportGenerated={state.reportGenerated}
                reportDataUri={state.reportDataUri}
                htmlReport={
                    <RecordPageListReportHtml
                        companyId={props.companyId}
                        report={state.report}
                        employeeTypes={state.employeeTypes}
                        jobTitles={state.jobTitles}
                        unitIds={state.unitIds}
                        activityState={state.activityState}
                        sortColumn={state.report
                            ? state.report.sortColumn
                            : ""}
                        sortOrderIsAsc={state.report
                            ? state.report.sortOrderIsAsc
                            : true}
                        onSetSortData={props.onSetSortData}
                    />
                }
                hidePageTitle={state.report?.type === ReportType.Html}
                sortingDisabled={true}
                multiSelect
                selectedId={state.selectedId}
                checkedIds={state.checkedIds}
                onSetSelectedIds={this.handleSetSelectedIds}
                onSetSortData={props.onSetSortData}
                onDownload={this.handleDownload}
            />
        );
    }
}